import React from 'react';
import { Button } from '@fluentui/react-components';
import MarkdownView from 'react-showdown';
import { useLocalization } from '../Helpers/useLocalization';

interface ICollapsibleDescriptionProps {
    markdown: string;
    className?: string;
    style?: React.CSSProperties;
    // When false/undefined the description always renders in full (no clamp, no toggle).
    collapsible?: boolean;
    // Collapsed height in px used while clamped (default 160).
    collapsedHeight?: number;
}

// The product/bundle description block, optionally clamped to a fixed height with a
// "Show more / Show less" toggle. The toggle only appears when the content actually
// exceeds the collapsed height — a short description shows no toggle and no clamp.
const CollapsibleDescription: React.FC<ICollapsibleDescriptionProps> = ({ markdown, className, style, collapsible, collapsedHeight = 160 }) => {
    const L = useLocalization();
    const [expanded, setExpanded] = React.useState(false);
    const [overflowing, setOverflowing] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    // Measure the full content height (scrollHeight ignores the maxHeight clamp) against the
    // collapsed cap. Re-measure when the content or configuration changes.
    React.useLayoutEffect(() => {
        if (!collapsible) { setOverflowing(false); return; }
        const el = ref.current;
        if (!el) return;
        setOverflowing(el.scrollHeight > collapsedHeight + 1);
    }, [collapsible, collapsedHeight, markdown]);

    const clamp = collapsible && overflowing && !expanded;

    return (
        <>
            <div
                ref={ref}
                className={className}
                style={{ ...style, ...(clamp ? { maxHeight: collapsedHeight, overflow: 'hidden' } : {}) }}
            >
                <MarkdownView markdown={markdown} options={{ tables: true, emoji: true }} />
            </div>
            {collapsible && overflowing && (
                <div style={{ margin: '0.25em 0 1em' }}>
                    <Button appearance='transparent' onClick={() => setExpanded(v => !v)}>
                        {expanded ? L('localization_show_less', 'Show less') : L('localization_show_more', 'Show more')}
                    </Button>
                </div>
            )}
        </>
    );
};

export default CollapsibleDescription;
